import { Component, Input, Output, EventEmitter, ViewChild, ChangeDetectorRef, AfterViewInit, ElementRef, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';

import { HisNavigationService } from '../../../core/service/base/his-navigation.service';
import { HisNotificationService } from '../../../core/service/notifications/his-notification.service';
import { HisDdDirective } from '../../../core/directive/his-dd.directive';
import { PaginationEvent } from './his-dialog-header-paginator.component';
import { HisDialogHeaderPaginatorService } from '../../../core/service/base/his-dialog-header-paginator.service';
import { Subscription } from 'rxjs';
import { HisSessionStorageKeys } from '../../../core/model/his-session-storage-keys';

@Component( {
    selector: 'his-dialog-header',
    templateUrl: './his-dialog-header.component.html',
    styleUrls: ['./his-dialog-header.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisDialogHeaderComponent implements AfterViewInit, OnInit, OnDestroy {
    private cdRef = inject( ChangeDetectorRef );
    private navigationService = inject( HisNavigationService );
    private notificationService = inject( HisNotificationService );
    private dialogHeaderPaginatorService = inject( HisDialogHeaderPaginatorService );


    // Soll die Datenbox angezeigt werden?
    @ViewChild( 'dataBox', { static: true } ) dataBox: ElementRef;
    showDataBox = false;

    @Input() showSaveButton = true;
    @Input() showDraftSaveButton = false;
    @Input() showDraftDeleteButton = false;
    @Input() showCustomButton = false;
    @Input() customButtonLabel: string;
    @Input() customButtonImage = '';
    @Input() customButtonClass = '';
    @Input() showSaveAndBackButton = false;
    @Input() showBackButton = true;
    @Input() showResetButton = true;
    @Input() showSearchButton = true;
    @Input() showChangeSearchButton = true;
    @Input() showNewSearchButton = true;

    // event emitter to notify the enveloping component that the "save" button was pressed
    @Output() save = new EventEmitter();
    @Output() saveDraft = new EventEmitter();
    @Output() deleteDraft = new EventEmitter();
    @Output() customEvent = new EventEmitter();
    // event emitter to notify the enveloping component that the "reset inputs" button was pressed
    @Output() resetInputs = new EventEmitter();

    /**
     * Event emitter to notify the enveloping component that the "paginator previous" button was pressed
     * IMPORTANT: result paginator will only be displayed if both paginatorPreviousElement and paginatorNextElement are observed
     */
    @Output() paginatorPreviousElement = new EventEmitter<PaginationEvent>();

    /**
     * Event emitter to notify the enveloping component that the "paginator next" button was pressed
     * IMPORTANT: result paginator will only be displayed if both paginatorPreviousElement and paginatorNextElement are observed
     */
    @Output() paginatorNextElement = new EventEmitter<PaginationEvent>();

    private lastButtonClickTimestamp = 0;
    private readonly DOUBLE_CLICK_DELAY = 500;
    private paginatorServiceSubscriptions$: Subscription[] = [];

    //paginator
    protected paginatorElements: any;
    protected paginatorIndex: number;
    protected renderPaginator = false;
    private changeSearchUrl: string;
    private newSearchUrl: string;

    ngOnInit() {
        this.navigationService.startDirtyTracking();
        this.renderPaginator = this.paginatorPreviousElement.observed && this.paginatorNextElement.observed;
        this.initializeNavigationContextFromService();

    }

    ngOnDestroy() {
        this.navigationService.stopDirtyTracking();
        this.dialogHeaderPaginatorService.clear();
        this.paginatorServiceSubscriptions$.forEach( s => s.unsubscribe() );
    }

    ngAfterViewInit() {
        // Die Datenbox soll nur angezeigt werden, wenn sie auch Inhalt hat. Die Implementierung der
        // Erkennung folgt https://stackoverflow.com/questions/38692881/how-to-check-whether-ng-content-exists
        this.showDataBox = this.dataBox.nativeElement && this.dataBox.nativeElement.children.length > 0;
        this.cdRef.detectChanges();
    }

    onSave( $event: Event ) {
        if ( Date.now() > this.lastButtonClickTimestamp + this.DOUBLE_CLICK_DELAY ) {
            this.lastButtonClickTimestamp = Date.now();
            this.notificationService.clear();
            this.save.emit( $event );
        }
        HisDdDirective.removeErrorIconInTab();
    }

    onSaveDraft( $event: Event ) {
        if ( Date.now() > this.lastButtonClickTimestamp + this.DOUBLE_CLICK_DELAY ) {
            this.lastButtonClickTimestamp = Date.now();
            this.notificationService.clear();
            this.saveDraft.emit( $event );
        }
        HisDdDirective.removeErrorIconInTab();
    }

    onDeleteDraft( $event: Event ) {
        if ( Date.now() > this.lastButtonClickTimestamp + this.DOUBLE_CLICK_DELAY ) {
            this.lastButtonClickTimestamp = Date.now();
            this.notificationService.clear();
            this.deleteDraft.emit( $event );
        }
    }

    onCustomEvent( $event: Event ) {
        if ( Date.now() > this.lastButtonClickTimestamp + this.DOUBLE_CLICK_DELAY ) {
            this.lastButtonClickTimestamp = Date.now();
            this.notificationService.clear();
            this.customEvent.emit( $event );
        }
    }

    onResetInputs( $event: Event ) {
        // "reset inputs" means the page must be restored from the database.
        // So all we can do here is to pass the event to the enveloping component.
        if ( Date.now() > this.lastButtonClickTimestamp + this.DOUBLE_CLICK_DELAY ) {
            this.lastButtonClickTimestamp = Date.now();
            this.resetInputs.emit( $event );
        }
        this.navigationService.dirty = false;
    }

    onBlur() {
        this.lastButtonClickTimestamp = 0;
    }

    back() {
        this.navigationService.navigateToLastBackUrl();
    }

    hasPreviousSearchPage() {
        return this.navigationService.hasPreviousSearchUrl();
    }

    backToSearch() {
        this.navigationService.navigateToLastSearchUrl();
    }

    hasPreviousSearchResultPage() {
        return this.navigationService.hasPreviousSearchResultUrl();
    }

    hasBackUrl() {
        return this.navigationService.hasLastBackUrl();
    }

    backToSearchResult() {
        this.navigationService.navigateToLastSearchResultUrl();
    }

    // called by save and back button - sends called as payload to inform parent component that
    // back() has to be called after saving is finished
    onSaveAndBack( $event: Event ) {
        if ( Date.now() > this.lastButtonClickTimestamp + this.DOUBLE_CLICK_DELAY ) {
            this.lastButtonClickTimestamp = Date.now();
            let called = true;
            this.save.emit( { $event, called } );
        }
    }

    onNewSearch() {
        // navigate back to search page
        return this.navigationService.navigateToUrl( this.newSearchUrl );
    }

    onChangeSearch() {
        sessionStorage.setItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON, 'CHANGE_SEARCH' );
        return this.navigationService.navigateToUrl( this.changeSearchUrl );
    }

    hasNewSearchUrl(): boolean {
        return !!this.newSearchUrl;
    }

    hasChangeSearchUrl(): boolean {
        return !!this.changeSearchUrl;
    }

    protected onPaginatorNextElement( event: PaginationEvent ) {
        this.paginatorIndex = event.newIndex;
        this.paginatorNextElement.emit( event );
    }

    protected onPaginatorPreviousElement( event: PaginationEvent ) {
        this.paginatorIndex = event.newIndex;
        this.paginatorPreviousElement.emit( event );
    }

    private initializeNavigationContextFromService(): void {
        this.paginatorServiceSubscriptions$.push(
            this.dialogHeaderPaginatorService.getData().subscribe( context => {
                if ( this.renderPaginator ) {
                    this.paginatorElements = context.ids;
                    this.paginatorIndex = context.index;
                }
                this.changeSearchUrl = context.changeSearchUrl;
                this.newSearchUrl = context.newSearchUrl;
            } )
        );
    }
}
